<template>
  <div>
    <a v-if="openNewTab" :href="to" target="_blank" :class="parentClass"
      ><slot></slot>{{ text }}</a
    >
    <div v-else-if="onClick" :class="parentClass" @click="onClick">
      <slot></slot>
      <p>{{ text }}</p>
    </div>
    <template v-else>
      <router-link
        :class="parentClass"
        :active-class="activeClassName"
        :to="`/${$i18n.locale}/${to}`"
        tag="div"
        @click.native="$store.dispatch('app/sidebar/collapse')"
        @keydown.enter.native="navigate()"
        :tabindex="isCollapsed && mobileMode ? -1 : 0"
      >
        <slot></slot>
        <span class="SidebarLink-copy">
          <p>{{ text }}</p>
        </span>
      </router-link>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  props: {
    to: {
      type: String,
      required: false,
    },
    icon: Object,
    text: {
      type: String,
      required: true,
    },
    openNewTab: {
      type: Boolean,
      default: false,
    },
    onClick: {
      type: Function,
      required: false,
    },
    // P3 visual-parity remediation (student-foundation-redesign, 00_*.md
    // §12): student-only nav-row/active-state geometry, matching the
    // rendered prototype's `.nav-item`/`.nav-item.active`. Volunteer keeps
    // the original `SidebarLink--active` treatment byte-for-byte -- this
    // prop only ever adds a class, never removes the existing one.
    isVolunteer: {
      type: Boolean,
      default: false,
    },
    // Optional row-level presentation variant. `'danger'` matches the
    // rendered prototype's `.nav-item--logout` red treatment (00_*.md
    // §13.1); null (default) is the plain nav row, used everywhere else
    // including every volunteer row.
    variant: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapState({ isCollapsed: state => state.app.sidebar.isCollapsed }),
    ...mapGetters({ mobileMode: 'app/mobileMode' }),
    size() {
      return this.mobileMode ? '1em' : '1.5em'
    },
    parentClass() {
      return {
        SidebarLink: true,
        'SidebarLink--desktop': !this.mobileMode,
        'SidebarLink--student': !this.isVolunteer,
        'SidebarLink--danger': this.variant === 'danger',
      }
    },
    activeClassName() {
      return this.isVolunteer
        ? 'SidebarLink--active'
        : 'SidebarLink--active SidebarLink--active-student'
    },
  },
  methods: {
    navigate() {
      this.$store.dispatch('app/sidebar/collapse')

      // necessary because router-link doesn't provide a way to set modifiers, like
      // keydown.enter.native, in the event prop
      this.$router.push(this.to)
    },
  },
}
</script>

<style lang="scss" scoped>
.SidebarLink {
  @include flex-container(row, initial, center);
  @include font-category('display-small');

  cursor: pointer;

  p {
    margin: 0;
    padding: 0;
  }

  &--active {
    p {
      color: $c-success-green;
    }
  }

  &--desktop {
    @include font-category('button');
  }

  // P3 visual-parity remediation (student-foundation-redesign, 00_*.md
  // §12): row geometry measured directly from the rendered prototype's
  // `.nav-item`/`.nav-item.active` (00_*.md §12 PF-09). Volunteer's
  // `--active` rule above is untouched -- this only ever adds rules under
  // a class volunteer rows never receive.
  &--student {
    border-radius: 14px;
    padding: 0 14px;
    height: 48px;
    font-size: 12px;
    font-weight: 700;
    color: #65718d;
  }

  &--active-student {
    background: #eef2ff;

    p {
      color: #243ab4;
    }
  }

  // Prototype's `.nav-item--logout` (00_*.md §13.1) -- red text/icon,
  // otherwise the same row geometry as `--student`.
  &--danger {
    color: #b42318;

    p {
      color: #b42318;
    }
  }
}

.SidebarLink-copy {
  min-width: 0;
}

a {
  @include font-category('subheading');
  color: $c-soft-black;
  display: block;

  &:hover {
    text-decoration: none;
  }
}
</style>
